import axios, { CancelTokenSource } from 'axios';
import { CheckboxSc } from 'components/final-form/CheckBox';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import React, { FC, FormEvent, ReactElement, useEffect, useRef, useState } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, CheckboxProps, Modal } from 'semantic-ui-react';
import { getOrderItemForBooking } from 'services/interventionOrderItemServices';
import styled from 'styled-components';
import { InterventionOrderItemInstanceDto, LabTestPanel } from 'ts-types/api.types';

const ContentDiv = styled.div`
  margin-top: 1rem;

  .actions-row {
    margin-top: 2rem;
  }

  .section-title {
    font-weight: bold;
    color: #222;
    margin-bottom: 0.5rem;
  }
`;

interface Props extends MessageSourceProps {
  bookingId?: number;
  orderItemInstance?: InterventionOrderItemInstanceDto;
  selectedPanelIds?: number[];
  open: boolean;
  onSave?: (selectedPanelIds: number[]) => void;
  close: () => void;
}

const UpsertOrderItemInstance: FC<Props> = (props: Props): ReactElement => {

  const {open, getMessage} = props;

  const [requiredDataLoaded, setRequiredDataLoaded] = useState<boolean>();
  const [selectedPanelIds, setSelectedPanelIds] = useState<number[]>([]);
  const [orderItemInstance, setOrderItemInstance] = useState<InterventionOrderItemInstanceDto>();

  const cancelTokenSource = useRef<CancelTokenSource>();
  if (!cancelTokenSource.current) {
    cancelTokenSource.current = axios.CancelToken.source();
  }

  useEffect(() => {
    const fetchData = async () => {
      const orderItemInstance = await getOrderItemForBooking(props.bookingId!, cancelTokenSource.current!);
      setOrderItemInstance(orderItemInstance);

      const panelIds = orderItemInstance.components.map(panel => panel.id);
      setSelectedPanelIds(panelIds);

      setRequiredDataLoaded(true);
    };

    const orderItemInstanceProp = props.orderItemInstance;
    if (!orderItemInstanceProp) {
      fetchData();
    } else {
      setOrderItemInstance(orderItemInstanceProp);

      const panelIds = props.selectedPanelIds
          ? props.selectedPanelIds
          : orderItemInstanceProp.components.map(panel => panel.id);
      setSelectedPanelIds(panelIds);

      setRequiredDataLoaded(true);
    }
  }, []);

  if (!open || !requiredDataLoaded || !orderItemInstance) {
    return <></>;
  }

  const renderPanelCheckbox = (panel: LabTestPanel) => {
    return <div>
      <CheckboxSc
          key={panel.code}
          label={panel.name}
          checked={selectedPanelIds.includes(panel.id)}
          onChange={(evt: FormEvent<HTMLInputElement>, data: CheckboxProps) => {
            if (selectedPanelIds.includes(panel.id)) {
              const newSelectedPanelCodes = selectedPanelIds.filter(panelId => panelId !== panel.id);
              setSelectedPanelIds(newSelectedPanelCodes);
            } else {
              setSelectedPanelIds([...selectedPanelIds, panel.id]);
            }
          }}
      />
    </div>;
  }

  const content = <ContentDiv>
    <div className="section-title">{getMessage("upsertOrderItemInstance.defaultPanels")}</div>
    {
      orderItemInstance.defaultComponents.map(panel => renderPanelCheckbox(panel))
    }

    {
        orderItemInstance.optionalComponents.length > 0 &&
        <>
          <div className="section-title">{getMessage("upsertOrderItemInstance.optionalPanels")}</div>
          {orderItemInstance.optionalComponents.map(panel => renderPanelCheckbox(panel))}
        </>
    }

    <div className="actions-row">
      <Button primary
              compact
              onClick={() => {
                props.onSave && props.onSave(selectedPanelIds);
                props.close();
              }}
      >
        {getMessage("button.save")}
      </Button>

      <Button compact
              onClick={props.close}
      >
        {getMessage("button.close")}
      </Button>
    </div>
  </ContentDiv>;

  return (
      <Modal
          open={open}
          onClose={() => props.close()}
          closeOnDimmerClick={false}
      >
        <Modal.Header>{getMessage("upsertOrderItemInstance.title")}</Modal.Header>
        <FormModalContentContainer>
          {content}
        </FormModalContentContainer>
      </Modal>
  );

};

export default withMessages(UpsertOrderItemInstance);
